import React from 'react';
import RouteDefinition from '../../helpers/Route/RouteDefinition';
import {
  ROLE_ADMINISTRATOR,
  ROLE_CEDAP_MANAGER,
  ROLE_CEDAP_MANAGER_READ_ONLY,
  ROLE_FACILITY_MANAGER,
  ROLE_GYNECOLOGIST,
  ROLE_LHC_MANAGER,
  ROLE_NEONATOLOGIST_BIRTH_CENTRE,
  ROLE_NEONATOLOGIST_NC,
  ROLE_OBSTETRICIAN,
  ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
  ROLE_REGIONAL_SUPERVISOR,
  ROLE_SUPPORT,
  NURSE_ROLES,
  NEONATOLOGIST_ROLES,
} from '../../constants/roles';
import { stringToBool } from '../../helpers/Object';

export const cedapDashboardRoute = new RouteDefinition({
  path: `/cedap/dashboard`,
  component: React.lazy(() => import('../mainDashboard')),
  roles: [
    ROLE_GYNECOLOGIST,
    ROLE_OBSTETRICIAN,
    ROLE_NEONATOLOGIST_BIRTH_CENTRE,
    ROLE_NEONATOLOGIST_NC,
    ...NURSE_ROLES,
  ],
  access: (user) =>
    (user.hasRole(NURSE_ROLES) && user.hcp?.config?.cedap?.cedapDelegation) ||
    !user.hasRole(NURSE_ROLES),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapDashboardStatusRoute = new RouteDefinition({
  path: '/cedap/dashboard/:status(compilation|reopened|to_validate|validated|rejected|archive|rectified)',
  component: React.lazy(() => import('../dashboard')),
  roles: [
    ROLE_GYNECOLOGIST,
    ROLE_OBSTETRICIAN,
    ROLE_NEONATOLOGIST_BIRTH_CENTRE,
    ROLE_NEONATOLOGIST_NC,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ...NURSE_ROLES,
  ],
  access: (user) =>
    (user.hasRole(NURSE_ROLES) && user.hcp?.config?.cedap?.cedapDelegation) ||
    !user.hasRole(NURSE_ROLES),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapManageRoute = new RouteDefinition({
  path: '/cedap/manage',
  component: React.lazy(() => import('../dashboard')),
  roles: [
    ROLE_REGIONAL_SUPERVISOR,
    ROLE_GYNECOLOGIST,
    ROLE_OBSTETRICIAN,
    ROLE_NEONATOLOGIST_BIRTH_CENTRE,
    ROLE_NEONATOLOGIST_NC,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ROLE_SUPPORT,
    ...NURSE_ROLES,
  ],
  access: (user) =>
    (user.hasRole(NURSE_ROLES) && user.hcp?.config?.cedap?.cedapDelegation) ||
    !user.hasRole(NURSE_ROLES),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapAdvancedSearchRoute = new RouteDefinition({
  path: '/cedap/advanced-search',
  component: React.lazy(() => import('../advancedSearch')),
  access: (user) =>
    user.hasRole([ROLE_REGIONAL_SUPERVISOR, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY]) ||
    (user.hasRole(ROLE_GYNECOLOGIST) && user.isLead),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapValidationRoute = new RouteDefinition({
  path: '/cedap/validation',
  component: React.lazy(() => import('../validation')),
  access: (user) =>
    user.hasRole([
      ROLE_REGIONAL_SUPERVISOR,
      ROLE_CEDAP_MANAGER,
      ROLE_CEDAP_MANAGER_READ_ONLY,
      ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
    ]) ||
    (user.hasRole([ROLE_GYNECOLOGIST, ROLE_NEONATOLOGIST_BIRTH_CENTRE, ROLE_NEONATOLOGIST_NC]) &&
      user.isLead),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapStatisticsRoute = new RouteDefinition({
  path: '/cedap/statistics',
  component: React.lazy(() => import('../statistics')),
  access: (user) =>
    user.hasRole([
      ROLE_REGIONAL_SUPERVISOR,
      ROLE_CEDAP_MANAGER,
      ROLE_CEDAP_MANAGER_READ_ONLY,
      ROLE_LHC_MANAGER,
      ROLE_FACILITY_MANAGER,
    ]) ||
    (user.hasRole(ROLE_GYNECOLOGIST) && user.isLead),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapAuthorizationRoutes = new RouteDefinition({
  path: '/cedap/authorization',
  component: React.lazy(() => import('../CedapAuthorization')),
  access: (user) => user.hasRole(NEONATOLOGIST_ROLES) && user.isLead,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapBirthStatisticsRoute = new RouteDefinition({
  path: '/cedap/birth-statistics',
  component: React.lazy(() => import('../statistics/birthStatistics')),
  access: (user) =>
    user.hasRole([ROLE_LHC_MANAGER, ROLE_FACILITY_MANAGER, ROLE_REGIONAL_SUPERVISOR]) ||
    (user.hasRole([ROLE_NEONATOLOGIST_BIRTH_CENTRE, ROLE_NEONATOLOGIST_NC]) && user.isLead),
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapCedapBirthStatisticsRoute = new RouteDefinition({
  path: `${cedapBirthStatisticsRoute.path}/cedap`,
  component: React.lazy(() => import('../statistics/birthStatistics/cedap')),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapSMABirthStatisticsRoute = new RouteDefinition({
  path: `${cedapBirthStatisticsRoute.path}/sma`,
  component: React.lazy(() => import('../statistics/birthStatistics/sma')),
  roles: [ROLE_REGIONAL_SUPERVISOR],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapBreastfeedingStatisticsRoute = new RouteDefinition({
  path: '/cedap/breastfeeding-statistics',
  component: React.lazy(() => import('../statistics/breastfeedingStatistics')),
  access: (user) => user.hasRole([ROLE_LHC_MANAGER, ROLE_FACILITY_MANAGER]),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const rppStatisticsRoute = new RouteDefinition({
  path: '/cedap/rpp-statistics',
  component: React.lazy(() => import('../statistics/rppStatistics')),
  roles: ROLE_REGIONAL_SUPERVISOR,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapAddSectionARoute = new RouteDefinition({
  path: '/cedap/:section(sectionA)/:operation(add)',
  component: React.lazy(() => import('../components/section/sectionAB')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN, ROLE_CEDAP_MANAGER],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapEditSectionARoute = new RouteDefinition({
  path: '/cedap/:cedapId/:section(sectionA)/:operation(edit)',
  component: React.lazy(() => import('../components/section/sectionAB')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN, ROLE_CEDAP_MANAGER],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapViewSectionARoute = new RouteDefinition({
  path: '/cedap/:cedapId/:section(sectionA)/:operation(view)',
  component: React.lazy(() => import('../components/section/sectionAB')),
  roles: [
    ROLE_GYNECOLOGIST,
    ROLE_OBSTETRICIAN,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
  ],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapAddEditSectionBRoute = new RouteDefinition({
  path: '/cedap/:cedapId/:section(sectionB|sectionC|sectionD)/:operation(add|edit)',
  component: React.lazy(() => import('../components/section/sectionAB')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN, ROLE_CEDAP_MANAGER],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapEditSectionBPreviousConceptions = new RouteDefinition({
  path: '/cedap/:cedapId/:section(sectionB)/edit-previous-conceptions',
  component: React.lazy(() => import('../components/section/sectionBPreviousConceptions')),
  roles: [ROLE_GYNECOLOGIST],
  access: (user) => user.isValidator,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapViewSectionBRoute = new RouteDefinition({
  path: '/cedap/:cedapId/:section(sectionB|sectionC|sectionD)/:operation(view)',
  component: React.lazy(() => import('../components/section/sectionAB')),
  roles: [
    ROLE_GYNECOLOGIST,
    ROLE_OBSTETRICIAN,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
  ],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapSectionsChildrenRoute = new RouteDefinition({
  path: '/cedap/:cedapId/:section(sectionsChildren)',
  component: React.lazy(() => import('../components/section/sectionAB')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

// export const cedapAddEditSectionDERoute = new RouteDefinition({
//   path: '/cedap/:cedapId/childId/:childId/:section(sectionD|sectionE|sectionF|sectionG)/:operation(add|edit|view)',
//   component: React.lazy(() => import('../components/section/newbornSection.switcher')),
//   roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
//   enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
// });

export const cedapSearchChildRoute = new RouteDefinition({
  path: '/cedap/child/search',
  component: React.lazy(() => import('../components/sectionC/page/CedapSearch')),
  roles: [
    ROLE_NEONATOLOGIST_BIRTH_CENTRE,
    ROLE_NEONATOLOGIST_NC,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ...NURSE_ROLES,
  ],
  access: (user) =>
    (user.hasRole(NURSE_ROLES) && user.hcp?.config?.cedap?.cedapDelegation) ||
    !user.hasRole(NURSE_ROLES),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapAddSectionCRoute = new RouteDefinition({
  path: '/cedap/:cedapId/childId/:childId/:section(sectionC|sectionD|sectionE|sectionF|sectionG)/:operation(add|edit|view)',
  component: React.lazy(() => import('../components/section/newbornSection.switcher')),
  roles: [
    ROLE_GYNECOLOGIST,
    ROLE_OBSTETRICIAN,
    ROLE_NEONATOLOGIST_NC,
    ROLE_NEONATOLOGIST_BIRTH_CENTRE,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ...NURSE_ROLES,
  ],
  access: (user) =>
    (user.hasRole(NURSE_ROLES) && user.hcp?.config?.cedap?.cedapDelegation) ||
    !user.hasRole(NURSE_ROLES),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapEditSectionCRoute = new RouteDefinition({
  path: '/cedap/:cedapId/child/:sdo/:section(sectionC|sectionD|sectionE|sectionF|sectionG)/:operation(add|edit|view)',
  component: React.lazy(() => import('../components/section/newbornSection.switcher')),
  roles: [
    ROLE_GYNECOLOGIST,
    ROLE_NEONATOLOGIST_BIRTH_CENTRE,
    ROLE_NEONATOLOGIST_NC,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
    ...NURSE_ROLES,
  ],
  access: (user) =>
    (user.hasRole(NURSE_ROLES) && user.hcp?.config?.cedap?.cedapDelegation) ||
    !user.hasRole(NURSE_ROLES),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapEditSectionDERoute = new RouteDefinition({
  path: '/cedap/:cedapId/childId/:childId/:section(sectionD|sectionE|sectionF|sectionG)/:operation(add|edit|view)/:role(neonatologist|referent_congenital_malformations)',
  component: React.lazy(() => import('../components/section/newbornSection.switcher')),
  roles: [
    ROLE_NEONATOLOGIST_NC,
    ROLE_NEONATOLOGIST_BIRTH_CENTRE,
    ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
    ROLE_CEDAP_MANAGER,
    ROLE_CEDAP_MANAGER_READ_ONLY,
    ...NURSE_ROLES,
  ],
  access: (user) =>
    (user.hasRole(NURSE_ROLES) && user.hcp?.config?.cedap?.cedapDelegation) ||
    !user.hasRole(NURSE_ROLES),
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapGenerateCobRoute = new RouteDefinition({
  path: '/cedap/:cedapId/cob/generate',
  component: React.lazy(() => import('../components/cob/GenerateCob')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapViewCobRoute = new RouteDefinition({
  path: '/cedap/:cedapId/cob/view',
  component: React.lazy(() => import('../components/cob/ViewCob')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapCobsListRoute = new RouteDefinition({
  path: '/cedap/cob/list',
  component: React.lazy(() => import('../components/cob/CobsList')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapCobsSearchAndCreateRoute = new RouteDefinition({
  path: '/cedap/cob/search',
  component: React.lazy(() => import('../components/cob/SearchAndCreateCob')),
  roles: [ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapManageNewsRoute = new RouteDefinition({
  path: '/cedap/news/manage',
  component: React.lazy(() => import('../components/news')),
  roles: [ROLE_ADMINISTRATOR, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapAddNewsRoute = new RouteDefinition({
  path: '/cedap/news/:operation(add)',
  component: React.lazy(() => import('../components/forms/news')),
  roles: [ROLE_ADMINISTRATOR, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapEditNewsRoute = new RouteDefinition({
  path: '/cedap/news/:newsId/:operation(edit)',
  component: React.lazy(() => import('../components/forms/news')),
  roles: [ROLE_ADMINISTRATOR, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const cedapSupportRoute = new RouteDefinition({
  path: '/cedap/help-desk',
  component: React.lazy(() => import('../pages/HelpDesk/Cedap')),
  roles: ROLE_SUPPORT,
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});
